import { clientService } from '../services/clientService.js';
import { useDataSync } from '../hooks/useDataSync';
import { transformClientToTable } from '../utils/dataTransforms';
import { validateForm } from '../utils/validators';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { RiUserAddLine } from "react-icons/ri";
import GenericTable from '../components/GenericTable';
import ModalForm from '../components/ModalForm';
import OrderDetails from '../components/OrderDetails';
import ClientDetails from '../components/modals/ClientDetails';
import VehicleDetails from '../components/modals/VehicleDetails';
import GenericFilters from '../components/GenericFilters';

import { notifyWarning, notifySuccess, notifyError } from '../utils/notifications';
import { UserContext } from '../utils/UserContext';

const Clients = () => {
    const { selectedWorkshop } = useContext(UserContext);
    const { data, setData, addItem, updateItem, deleteItem } = useDataSync([]);
    const [workshopID, setWorkshopID] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
    const [clientIdToDelete, setClientIdToDelete] = useState(null);

    // Función para obtener los clientes desde la API
    const fetchClients = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await clientService.getClients();
            // Usar transformación reutilizable
            const clients = response.map(transformClientToTable);
            setData(clients);
            setFilteredData(clients);
        } catch (error) {
            console.error('Error al cargar clientes:', error);
            notifyError('Error al cargar los clientes');
        } finally {
            setIsLoading(false);
        }
    }, [setData]);

    useEffect(() => {
        if (selectedWorkshop) {
            setWorkshopID(selectedWorkshop._id);
        } else {
            notifyWarning("No se ha seleccionado un taller.");
        }
        if (workshopID) {
            fetchClients();
        }
    }, [selectedWorkshop, workshopID, fetchClients]);

    // Sincronizar filteredData cuando data cambia
    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    const columns = React.useMemo(() => [
        {
            Header: 'Nombre',
            accessor: 'nombreCliente',
            Cell: ({ row }) => {
                const firstName = row.original.client_name ? row.original.client_name.split(' ')[0] : 'N/A';
                const lastName = row.original.first_lastname || '';
                return <span>{`${firstName} ${lastName}`.trim()}</span>;
            }
        },
        { Header: 'Correo Electrónico', accessor: 'email' },
        { Header: 'Teléfono', accessor: 'phone' },
        { Header: 'Dirección', accessor: 'address' },
    ], []);

    const actionHandlers = {
        edit: (client) => handleViewDetails(client),
        delete: (client) => setClientIdToDelete(client.id)
    };

    const clientFormFields = [
        { name: 'client_name', label: 'Nombre(s)', type: 'text', required: true },
        { name: 'first_lastname', label: 'Primer Apellido', type: 'text', required: true },
        { name: 'second_lastname', label: 'Segundo Apellido', type: 'text', required: false },
        { name: 'company_name', label: 'Nombre de Empresa (opcional)', type: 'text', required: false },
        { name: 'email', label: 'Correo Electrónico', type: 'email', required: true },
        { name: 'phone', label: 'Teléfono', type: 'tel', required: true },
        { name: 'second_phone', label: 'Teléfono Secundario', type: 'tel', required: false },
        { name: 'address', label: 'Dirección', type: 'text', required: true },
        { name: 'RFC', label: 'RFC', type: 'text', required: false },
    ];

    const handleViewDetails = (rowData) => {
        setSelectedClient(rowData);
        setIsDetailModalOpen(true);
    }

    const handleViewVehicle = (vehicleData) => {
        setSelectedVehicle(vehicleData);
        setIsVehicleModalOpen(true);
    }

    const handleViewOrder = (orderData) => {
        // Mapear los datos al formato que espera OrderDetails
        const mappedOrder = {
            noOrden: orderData.id,
            workshopID: workshopID,
            fechaIngreso: orderData.checkin_date || orderData.fechaIngreso,
            serviciosSolicitados: orderData.services_request || orderData.serviciosSolicitados,
            estadoProceso: orderData.process_state || orderData.estadoProceso,
            total: orderData.total
        };

        setSelectedOrder(mappedOrder);
        setIsOrderModalOpen(true);
    }

    const updateOrderData = (field, value) => {
        setSelectedOrder(prev => {
            const updated = { ...(prev ?? {}), [field]: value };
            return updated;
        });
    };

    const handleCloseOrderModal = () => {
        setIsOrderModalOpen(false);
        setSelectedOrder(null);
    }

    const handleCloseVehicleModal = () => {
        setIsVehicleModalOpen(false);
        setSelectedVehicle(null);
        // Si el modal de orden está abierto, también cerrarlo
        if (isOrderModalOpen) {
            handleCloseOrderModal();
        }
    }

    const handleCloseClientModal = () => {
        setIsDetailModalOpen(false);
        setSelectedClient(null);
        // Si el modal de vehículo está abierto, también cerrarlo
        if (isVehicleModalOpen) {
            handleCloseVehicleModal();
        }
    }

    const handleAddClient = async (formData) => {
        const { client_name, first_lastname, email, address, phone } = formData;

        // Validar campos obligatorios usando el validador centralizado
        const validation = validateForm({
            client_name,
            first_lastname,
            email,
            address,
            phone
        }, ['client_name', 'first_lastname', 'email', 'address', 'phone']);

        if (!validation.valid) {
            const errorMessages = Object.values(validation.errors).join(', ');
            notifyWarning(errorMessages);
            return;
        }

        try {
            const clientData = {
                name: client_name,
                first_lastname: first_lastname,
                second_lastname: formData.second_lastname || '',
                email: email,
                address: address,
                phone: phone,
                RFC: formData.RFC || '',
                company_name: formData.company_name || '',
                second_phone: formData.second_phone || '',
            };

            // Crear el cliente en la API
            await clientService.createClient(clientData);

            // Refrescar la lista de clientes desde la API para asegurar datos correctos
            await fetchClients();

            setIsAddClientModalOpen(false);
            notifySuccess('Cliente agregado correctamente.');
        } catch (error) {
            console.error('Error al agregar cliente:', error);
            notifyError('Error al agregar el cliente');
        }
    };

    const handleUpdateClient = (clientId, updatedData) => {
        // Mapear los datos actualizados al formato de la tabla
        const mappedUpdate = {
            client_name: updatedData.name,
            first_lastname: updatedData.first_lastname,
            second_lastname: updatedData.second_lastname || '',
            email: updatedData.email,
            phone: updatedData.phone,
            second_phone: updatedData.second_phone || '',
            address: updatedData.address,
            rfc: updatedData.RFC,
            company_name: updatedData.company_name || '',
        };

        updateItem(clientId, mappedUpdate);
    };

    const handleDeleteClient = async () => {
        if (!clientIdToDelete) return;

        try {
            await clientService.deleteClient(
                clientIdToDelete,
                { is_deleted: 1 },
            );
            deleteItem(clientIdToDelete);
            notifySuccess('Cliente eliminado correctamente.');
            setClientIdToDelete(null);
        } catch (error) {
            console.error('Error al eliminar cliente:', error);
            notifyError('No se pudo eliminar el cliente.');
        }
    };

    const handleCancelDelete = () => {
        setClientIdToDelete(null);
    };

    const handleFilterChange = (filters) => {
        // OrderFilters devuelve { search: "...", status: "..." }
        // Para Clients solo usamos search
        if (!filters.search || filters.search.trim() === '') {
            setFilteredData(data);
            return;
        }

        const lowerTerm = filters.search.toLowerCase();
        const filtered = data.filter(client => {
            const searchIn = [
                client.client_name || '',
                client.first_lastname || '',
                client.email || '',
                client.phone || '',
                client.address || ''
            ].join(' ').toLowerCase();

            return searchIn.includes(lowerTerm);
        });

        setFilteredData(filtered);
    };

    const handleClearFilters = () => {
        setFilteredData(data);
    };

    return (
        <div className='category-container'>
            <div className="category-header">
                <h1 className="category-title">Clientes</h1>
                <button
                    className="add-btn add-btn-icon"
                    onClick={() => setIsAddClientModalOpen(true)}
                >
                    <RiUserAddLine aria-hidden="true" />
                    <span>Nuevo cliente</span>
                </button>
            </div>
            <GenericFilters
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                searchPlaceholder="Nombre, email, teléfono, dirección..."
                filterLabel="Buscar Clientes"
            />
            <GenericTable
                columns={columns}
                data={filteredData}
                isLoading={isLoading}
                actionList={['edit', 'delete']}
                actionHandlers={actionHandlers}
            />
            {isDetailModalOpen && selectedClient && (
                <ModalForm
                    isOpen={isDetailModalOpen}
                    onClose={handleCloseClientModal}
                    title="Detalles del Cliente"
                    size="large"
                    footer={
                        <button className="cancel-btn" onClick={handleCloseClientModal}>
                            Cerrar
                        </button>
                    }
                >
                    <ClientDetails
                        clientData={selectedClient}
                        workshopID={workshopID}
                        onViewVehicle={handleViewVehicle}
                        onClose={handleCloseClientModal}
                        onUpdate={handleUpdateClient}
                    />
                </ModalForm>
            )}
            {isVehicleModalOpen && selectedVehicle && (
                <ModalForm
                    isOpen={isVehicleModalOpen}
                    onClose={handleCloseVehicleModal}
                    title="Detalles del Vehículo"
                    size="large"
                    className="stacked"
                    footer={
                        <button className="cancel-btn" onClick={handleCloseVehicleModal}>
                            Cerrar
                        </button>
                    }
                >
                    <VehicleDetails
                        vehicleData={selectedVehicle}
                        workshopID={workshopID}
                        onViewOrder={handleViewOrder}
                    />
                </ModalForm>
            )}
            {isOrderModalOpen && selectedOrder && (
                <ModalForm
                    isOpen={isOrderModalOpen}
                    onClose={handleCloseOrderModal}
                    title={`Orden de Trabajo: ${selectedOrder.noOrden}`}
                    size="large"
                    className="stacked"
                    footer={
                        <button className="cancel-btn" onClick={handleCloseOrderModal}>
                            Cerrar
                        </button>
                    }
                >
                    <OrderDetails
                        isOpen={isOrderModalOpen}
                        onClose={handleCloseOrderModal}
                        orderData={selectedOrder}
                        updateOrderData={updateOrderData}
                    />
                </ModalForm>
            )}
            {/* Modal para añadir cliente */}
            {isAddClientModalOpen && (
                <ModalForm
                    isOpen={isAddClientModalOpen}
                    onClose={() => setIsAddClientModalOpen(false)}
                    onSubmit={handleAddClient}
                    title="Agregar Nuevo Cliente"
                    size="medium"
                    fields={clientFormFields}
                />
            )}
            {/* Modal de confirmación para eliminar cliente */}
            {clientIdToDelete && (
                <ModalForm
                    isOpen={true}
                    onClose={handleCancelDelete}
                    title="Confirmar Eliminación"
                    size="small"
                    footer={
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button className="cancel-btn" onClick={handleCancelDelete}>
                                Cancelar
                            </button>
                            <button
                                className="delete-btn"
                                style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}
                                onClick={handleDeleteClient}
                            >
                                Eliminar
                            </button>
                        </div>
                    }
                >
                    <p>¿Estás realmente seguro de que deseas eliminar este cliente? Esta acción no se puede deshacer.</p>
                </ModalForm>
            )}
        </div>
    );
};
export default Clients;